import type { Directive } from 'vue';
import {
    validateAccessCanEditLibrary,
    validateAccessCanEditCode,
    validateAccessCanEditTagByName,
    validateAccessCanTransitionRelease,
    validateAccessCanEditCodeForAllScopes
} from '@/shared/helpers/api';
import { memoize } from '@/shared/helpers/timedMemoize';

function toggleAttribute(el: HTMLElement, attributeName: string, value: boolean) {
    if (value) {
        el.setAttribute(attributeName, '');
    } else {
        el.removeAttribute(attributeName);
    }
}

class Tree {
    static all = new Set<Tree>();
    static isRefreshQueued = false;
    static count = 0;

    root: HTMLElement;
    hasAccess: boolean | null;
    shouldHide: boolean;
    isDirty = false;
    originalDisabledStates = new WeakMap<HTMLElement, boolean>();
    observer: MutationObserver;

    constructor(root: HTMLElement, hasAccess: boolean | null, shouldHide: boolean) {
        Tree.all.add(this);

        this.root = root;
        this.hasAccess = hasAccess;
        this.shouldHide = shouldHide;
        this.isDirty = true;
        this.observer = new MutationObserver((mutations) => {
            this.processMutations(mutations);
            Tree.refresh();
        });
        this.observer.observe(root, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['disabled'],
            attributeOldValue: false
        });

        Tree.refresh();
    }

    dispose() {
        this.observer.disconnect();
        Tree.all.delete(this);
    }

    processMutations(mutations: MutationRecord[]) {
        for (const mutation of mutations) {
            const target = mutation.target;

            if (mutation.attributeName === 'disabled' && target instanceof HTMLElement) {
                this.originalDisabledStates.set(
                    target,
                    target.hasAttribute('disabled')
                );
            }

            this.isDirty = true;
        }
    }

    update(hasAccess: boolean | null, shouldHide: boolean) {
        this.hasAccess = hasAccess;
        this.shouldHide = shouldHide;
        this.isDirty = true;
        Tree.refresh();
    }

    static refresh() {
        if (Tree.isRefreshQueued) {
            return;
        }
        Tree.isRefreshQueued = true;

        requestAnimationFrame(() => {
            Tree.processPendingMutations();
            try {
                for (const tree of Tree.all) {
                    tree.reapplyDisabled();
                }
            } finally {
                Tree.discardPendingMutations();
                Tree.isRefreshQueued = false;
            }
        });
    }

    setDisabledAttribute(el: HTMLElement) {
        if (!this.originalDisabledStates.has(el)) {
            this.originalDisabledStates.set(el, el.hasAttribute('disabled'));
        }

        const isOriginallyDisabled = this.originalDisabledStates.get(el);

        toggleAttribute(el, 'disabled', !this.hasAccess || isOriginallyDisabled === true);
    }

    reapplyDisabled() {
        if (!this.isDirty) {
            return;
        }

        if (this.shouldHide) {
            this.root.hidden = !this.hasAccess;
        } else {
            const buttonElements = this.root.getElementsByTagName('button');
            if (buttonElements.length) {
                for (const button of buttonElements) {
                    this.setDisabledAttribute(button);
                    button.title = this.hasAccess !== false ? '' : 'You are not authorized for this action';
                }
            } else if (this.root instanceof HTMLButtonElement) {
                this.setDisabledAttribute(this.root);
                this.root.title = this.hasAccess !== false ? '' : 'You are not authorized for this action';
            } else if (this.root instanceof HTMLAnchorElement) {
                this.setDisabledAttribute(this.root);
                this.root.style.pointerEvents = this.hasAccess ? 'auto' : 'none';
                this.root.title = this.hasAccess !== false ? '' : 'You are not authorized for this action';
            } else if (this.root instanceof HTMLLabelElement) {
                this.setDisabledAttribute(this.root);
                this.root.style.cursor = this.hasAccess ? 'pointer' : 'no-drop';
                this.root.title = this.hasAccess !== false ? '' : 'You are not authorized for this action';
                const firstChild = this.root.childNodes[0];
                if (firstChild instanceof HTMLElement) {
                    this.setDisabledAttribute(firstChild);
                }
            } else {
                this.root.style.display = this.hasAccess ? '' : 'none';
            }
        }

        this.isDirty = false;
    }

    static discardPendingMutations() {
        for (const tree of Tree.all) {
            tree.observer.takeRecords();
        }
    }

    static processPendingMutations() {
        for (const tree of Tree.all) {
            tree.processMutations(tree.observer.takeRecords());
        }
    }
}

const trees = new WeakMap<HTMLElement, Tree>();

function getTree(el: HTMLElement) {
    const tree = trees.get(el);
    if (!tree) {
        throw new Error('Access directive tree was not initialized');
    }
    return tree;
}

export function createAccessDirective<V>(
    checkAccess: (value: V) => Promise<boolean | null> | boolean | null
): Directive<HTMLElement, V> {
    return {
        async mounted(el, binding) {
            const shouldHide = Boolean(binding.arg);
            const accessSpec = binding.value;

            const tree = new Tree(el, false, shouldHide);
            trees.set(el, tree);
            const hasAccess = await checkAccess(accessSpec);
            tree.update(hasAccess, shouldHide);
        },
        async updated(el, binding) {
            const shouldHide = Boolean(binding.arg);
            const accessSpec = binding.value;
            const tree = getTree(el);
            try {
                const hasAccess = await checkAccess(accessSpec);
                tree.update(hasAccess, shouldHide);
            } catch (e) {
                tree.update(false, shouldHide);
                throw e;
            }
        },
        unmounted(el) {
            const tree = getTree(el);
            trees.delete(el);

            tree.dispose();
        }
    };
}

async function hasPermission(requiredPermission: string) {
    return await window.authService.hasPermission(requiredPermission);
}

async function hasAnyPermission(requiredPermissions: string[]) {
    return await window.authService.hasAnyPermission(requiredPermissions);
}

export const requireAnyPermission = createAccessDirective(hasAnyPermission);

const memoizationOptions = {
    debug: false,
    timeout: 30 * 1000
};

// Note on possible performance optimization on permission checks:
// In this and the other methods that delegate to the backend for access validation we could
// short circuit for administrators. This would make it less likely that the developers (whom
// are all admins) would notice if something is wrong with the authorization code, so it should be avoided.
// The code would look something like this:
// if (await hasPermission('IsAdministrator'))
//     return true;

export const isAdministrator = memoize(
    () => hasPermission('IsAdministrator'),
    memoizationOptions
);

const canCreateCodeImport = memoize(
    () => hasPermission('CanEditRelease'),
    memoizationOptions
);

const canCreateLibrary = memoize(
    () => hasPermission('CanEditLibrary'),
    memoizationOptions
);

export const canEditLibrary = memoize(
    (libraryName: string) => validateAccessCanEditLibrary(window.authService, libraryName),
    memoizationOptions
);

export type CanEditCodeOptions = {
    libraryName: string;
    scopes?: string[];
};

function canEditCodeOptionsKey(options: CanEditCodeOptions) {
    return `${options.libraryName}|${options.scopes ? options.scopes.join('-') : null}`;
}

export const canEditCode = memoize(
    (options: CanEditCodeOptions) => validateAccessCanEditCode(window.authService, options.libraryName, options.scopes ?? []),
    { ...memoizationOptions, cacheKeyFactory: canEditCodeOptionsKey }
);

async function canEditCodeFromDirective(options?: CanEditCodeOptions) {
    if (!options) {
        return false;
    }

    return await canEditCode(options);
}

export const canEditTag = memoize(
    (tag: string) => validateAccessCanEditTagByName(window.authService, tag),
    memoizationOptions
);

const canTransitionRelease = memoize(
    (releaseId: number) => validateAccessCanTransitionRelease(window.authService, releaseId),
    memoizationOptions
);

export const codeEditPermissionsForLibrary = memoize(
    (libraryName: string) => validateAccessCanEditCodeForAllScopes(window.authService, libraryName),
    memoizationOptions
);

export const requireIsAdministrator = createAccessDirective(isAdministrator);
export const requireCanCreateCodeImport = createAccessDirective(canCreateCodeImport);
export const requireCanCreateLibrary = createAccessDirective(canCreateLibrary);
export const requireCanEditLibrary = createAccessDirective(canEditLibrary);
export const requireCanEditTag = createAccessDirective(canEditTag);
export const requireCanTransitionRelease = createAccessDirective(canTransitionRelease);
export const requireCanEditCode = createAccessDirective(canEditCodeFromDirective);
